import React from 'react';
import { ConfiguratorUIElementStates } from '@/elements/base/ElementState';
import { ContainerState } from '@/elements/base/ContainerState';
import {
  MockTextAttributeState,
  MockTextAttributeConfig,
  MockBooleanAttributeState,
  MockBooleanAttributeConfig,
  MockIntegerAttributeState,
  MockIntegerAttributeConfig,
} from './MockAttributeStates';
import {
  MockComponentLookup,
  MockComponentLookupConfig,
  MockCompositeProductInfoState,
  MockCompositeProductInfoConfig,
} from './MockComponentStates';

// ============================================================================
// Text Attribute Factory
// ============================================================================

/**
 * Creates a mock text attribute child element for container components.
 * @param config - Configuration for the text attribute including view element and text data
 * @returns ConfiguratorUIElementStates with elementKind/elementType for ElementRenderer
 */
export function createMockTextAttributeChild(config: MockTextAttributeConfig): ConfiguratorUIElementStates {
  return new MockTextAttributeState(config) as unknown as ConfiguratorUIElementStates;
}

// ============================================================================
// Boolean Attribute Factory
// ============================================================================

/**
 * Creates a mock boolean attribute child element for container components.
 * @param config - Configuration for the boolean attribute including view element and text data
 * @returns ConfiguratorUIElementStates with elementKind/elementType for ElementRenderer
 */
export function createMockBooleanAttributeChild(config: MockBooleanAttributeConfig): ConfiguratorUIElementStates {
  return new MockBooleanAttributeState(config) as unknown as ConfiguratorUIElementStates;
}

// ============================================================================
// Integer Attribute Factory
// ============================================================================

/**
 * Creates a mock integer attribute child element for container components.
 * @param config - Configuration for the integer attribute including view element and text data
 * @returns ConfiguratorUIElementStates with elementKind/elementType for ElementRenderer
 */
export function createMockIntegerAttributeChild(config: MockIntegerAttributeConfig): ConfiguratorUIElementStates {
  return new MockIntegerAttributeState(config) as unknown as ConfiguratorUIElementStates;
}

// ============================================================================
// Component Factory
// ============================================================================

export interface MockComponentConfig {
  viewElement: any;
  lookupConfig: MockComponentLookupConfig;
}

/**
 * Creates a mock component child element for container components.
 * The mock has the shape expected by ComponentCfgUIElement: lookup, productLookup, viewElement, isViewExist.
 * ElementRenderer looks up the component via elementKind='component' and elementType='default'.
 * @param config - Configuration for the component including view element and lookup config
 * @returns ConfiguratorUIElementStates with elementKind/elementType for ElementRenderer
 */
export function createMockComponentChild(config: MockComponentConfig): ConfiguratorUIElementStates {
  const lookup = new MockComponentLookup(config.lookupConfig);
  return {
    id: config.viewElement.id || 'mock-component',
    elementKind: 'component',
    elementType: 'default',
    viewElement: config.viewElement,
    lookup,
    productLookup: {
      isEnable: () => false,
      initialized: false,
      productData: [],
      canBeDisable: false,
    },
    isViewExist: true,
  } as unknown as ConfiguratorUIElementStates;
}

// ============================================================================
// Composite Product Info Factory
// ============================================================================

/**
 * Creates a mock composite product info child element for container components.
 * ElementRenderer looks up the component via elementKind='ui-element' and elementType='composite-product-info'.
 * @param config - Configuration for the composite product info including view element and product data
 * @returns ConfiguratorUIElementStates with elementKind/elementType for ElementRenderer
 */
export function createMockCompositeProductInfoChild(
  config: MockCompositeProductInfoConfig,
): ConfiguratorUIElementStates {
  return new MockCompositeProductInfoState(config) as unknown as ConfiguratorUIElementStates;
}

// ============================================================================
// Container Factory (for nesting)
// ============================================================================

/**
 * Options for configuring container level hierarchy in mocks.
 * Mirrors the level calculation from virtualLayoutBuilder.ts.
 */
export interface MockContainerLevelOptions {
  /**
   * The nesting level of this container (1-3).
   * - Root container and its direct children: level 1
   * - Children of level 1 containers: level 2
   * - Children of level 2+ containers: level 3 (capped)
   */
  level?: number;
  /**
   * The level of the parent container (for title styling).
   * Container titles use parent's level because the title
   * visually appears in the parent's context.
   */
  parentLevel?: number;
}

/**
 * Creates a mock container child element for nested container structures.
 * Uses a real ContainerState so that instanceof checks pass in ElementRenderer.
 * The ContainerComponent parameter is kept for API compatibility but is no longer used directly -
 * ElementRenderer looks up the right component from the registry using viewElement.type.
 * @param ContainerComponent - The container component (kept for API compatibility, not used directly)
 * @param viewElement - The view element configuration for the container
 * @param children - Array of child elements to render inside the container
 * @param levelOptions - Optional level configuration for proper styling hierarchy
 * @returns ConfiguratorUIElementStates backed by a real ContainerState
 */
export function createMockContainerChild<T extends React.ComponentType<any>>(
  ContainerComponent: T,
  viewElement: any,
  children: ConfiguratorUIElementStates[],
  levelOptions?: MockContainerLevelOptions,
): ConfiguratorUIElementStates {
  // Apply level properties to viewElement if provided
  const viewElementWithLevel = levelOptions
    ? {
        ...viewElement,
        _level: levelOptions.level,
        _parentLevel: levelOptions.parentLevel,
      }
    : viewElement;

  return new ContainerState(children, viewElementWithLevel) as ConfiguratorUIElementStates;
}
